import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import {
  PAGE_SLIDE_MS,
  pushInSx,
  shouldPushDesktop,
  shouldSlide,
  slideInSx,
  slideOutSx,
  type SlideNavState,
} from 'utils/pageTransition'
import { scrollToTop } from 'utils/scrollRestore'

/**
 * The push transition, for any page reached by tapping a row on a phone.
 *
 * ## The rule this implements
 *
 * A page you PUSH INTO enters from the RIGHT and leaves back out to the right.
 * That is the whole convention: forward is deeper, so the new screen comes from
 * the side you are heading toward, and back reverses it. It applies to every
 * chevron row that navigates — a certificate, its Provenance / Scans / Activity
 * screens, Profile's Notifications row — and it is what makes the back gesture
 * legible without reading a label, because the motion already said which way
 * the stack went.
 *
 * MOBILE ONLY, via `shouldSlide()`. A desktop viewport is not a stack of cards,
 * there is no thumb travelling in a direction, and a full-width slide on a
 * 1280px window is a lot of motion for a navigation the pointer made
 * deliberately. `shouldSlide()` also returns false under
 * `prefers-reduced-motion`, so the OS setting is honoured in one place rather
 * than at each call site.
 *
 * ## Why a hook rather than a component
 *
 * Three surfaces had already hand-rolled this exact pair — the certificate page,
 * CertificateSubView and SharePage — each with its own `exiting` state, its own
 * ref guard and its own `setTimeout`. That is the second-hand-roll threshold
 * from working-conventions §36, and the copies had already drifted.
 *
 * SharePage was the one deliberate divergence — it ROSE from the bottom, on the
 * argument that Share is a task you open and dismiss rather than a level deeper.
 * That argument does not survive the surface being a full PAGE: the rise is the
 * bottom-sheet gesture, and this app has real bottom sheets (`DrawerPopup`) one
 * tap away on the same screen, so a full page using their motion claimed to be a
 * sheet and then was not one. It is a pushed screen with a back-link like every
 * other, and now moves like one.
 *
 * SharePage was the only caller of `riseInSx` / `riseOutSx`, so both have since
 * been deleted from `utils/pageTransition` — an unused export is not a design
 * decision, it is a leftover.
 *
 * ## The two halves, and why the SENDER matters
 *
 * `slideIn` is set by the LINK, not inferred on arrival. Inferring it would
 * animate the most common entry into a certificate — an NFC tap on a physical
 * tag, a cold load with no previous screen — so the page would slide away from
 * nothing. Use `pushState()` on the navigating side; this hook reads it.
 */
export interface PushPageOptions {
  /**
   * This route can ONLY be arrived at by a push, so skip the sender flag and
   * animate whenever `shouldSlide()` allows.
   *
   * The `slideIn` flag exists to protect ONE case: a cold load with no previous
   * screen, which is how a physical NFC tap enters the certificate page. A route
   * with no such entry has nothing to protect — the app runs on a `MemoryRouter`,
   * so `/certificates/:id/share` has no URL anyone can type, bookmark or scan,
   * and every path to it comes from a screen already on the stack.
   *
   * Set this ONLY when that is actually true. It is a statement about the route,
   * not a preference: on a page that CAN be entered cold it reintroduces exactly
   * the bug the flag was added for.
   */
  pushOnly?: boolean
  /**
   * Also push on DESKTOP — a short 24px directional arrival instead of an
   * instant swap. VERA-315 prototype, currently opted into by the certificate
   * sub-views only.
   *
   * ## Entrance ONLY, deliberately
   *
   * The mobile transition is a pair: the leaving page animates out, the caller
   * waits `PAGE_SLIDE_MS`, then navigates. That wait is affordable on a phone
   * because the motion IS the navigation's feedback. On desktop it would add
   * 180ms of dead time to a click that a pointer user made deliberately and
   * already knows the outcome of — paying latency for polish, on the device with
   * the least tolerance for it.
   *
   * So desktop animates only the ARRIVING page. The directional cue still lands
   * (the new page enters from the side the stack moved), and nothing is delayed:
   * `goBack` navigates immediately, exactly as it does today.
   *
   * Opt-in rather than automatic because it is a prototype. Turning it on
   * everywhere would also cover `SharePage` and Profile -> Notifications, which
   * have not been looked at.
   */
  desktopPush?: boolean
}

export const usePushPage = (
  goBackTo: string | (() => void),
  { pushOnly = false, desktopPush = false }: PushPageOptions = {},
) => {
  const navigate = useNavigate()
  const location = useLocation()

  const [exiting, setExiting] = useState(false)
  /* Guards a second tap while the exit is playing from queueing a second
     navigate — the first one has not unmounted this page yet. */
  const exitingRef = useRef(false)

  const slideIn = pushOnly
    ? shouldSlide()
    : Boolean((location.state as SlideNavState | null)?.slideIn)

  /*
   * A pushed page lands at the TOP.
   *
   * The document scroll position survives a route change, so pushing from a
   * scrolled parent dropped the reader into the middle of the new page — caught
   * on Profile -> Notifications, which arrived with its own title already
   * scrolled off. The two pages share no vertical structure, so the inherited
   * offset means nothing on the new one.
   *
   * `CertificateSubView` already did this by hand; it belongs here, with the
   * rest of the push behaviour, so a page cannot adopt the transition and
   * forget the scroll. Coming BACK is deliberately not touched — the parent
   * restores its own position from `scrollRestore`, which is what returns you
   * to the row you tapped.
   */
  useEffect(() => {
    scrollToTop()
  }, [])

  const goBack = () => {
    if (exitingRef.current) return
    /*
     * Tell the destination it is being returned TO, so it enters from the left
     * and the stack visibly unwinds.
     *
     * Without this the child played its full 260ms exit to the right and the
     * parent then simply appeared — motion, then a hard cut, which reads as the
     * whole page flashing rather than as going back. The exit was animated and
     * the entrance was not, so the two halves of one gesture disagreed.
     *
     * Only when `shouldSlide()` — off mobile, and under `prefers-reduced-motion`,
     * there is no exit animation either, so signalling an entrance would
     * introduce motion on exactly the surfaces that opted out of it.
     *
     * A caller that passed its own `goBackTo` FUNCTION owns its navigation
     * entirely, so nothing is injected there; it is that caller's job to carry
     * the state if it wants the same behaviour.
     */
    const leave = () =>
      typeof goBackTo === 'function'
        ? goBackTo()
        : navigate(
            goBackTo,
            // The parent needs the flag on EITHER path — it is what tells it to
            // enter from the left. On desktop there is no exit animation to wait
            // for, but the direction still has to be communicated.
            shouldSlide() || (desktopPush && shouldPushDesktop())
              ? { state: { slideBack: true } }
              : undefined,
          )

    /*
     * The exit has to PLAY BEFORE the route changes. `navigate()` unmounts this
     * page on the same tick, so an animation started at click time has nothing
     * left to animate — which is why every one of these surfaces ended up with
     * a timeout rather than an `onAnimationEnd`.
     */
    if (!shouldSlide()) {
      leave()
      return
    }
    exitingRef.current = true
    setExiting(true)
    window.setTimeout(leave, PAGE_SLIDE_MS)
  }

  return {
    /*
     * Mobile keeps the full pair (exit, then entrance). Desktop gets the
     * entrance only — see `desktopPush`. `shouldSlide()` and
     * `shouldPushDesktop()` are mutually exclusive by their width tests, so the
     * two branches can never both arm.
     */
    pageSx: exiting
      ? slideOutSx(true)
      : slideIn
        ? slideInSx(true)
        : pushInSx(desktopPush && shouldPushDesktop(), 'right'),
    /** Pass to `PageHeader`'s `onBack`. */
    goBack,
    /** True while the exit is playing — for disabling controls if a page needs to. */
    exiting,
  }
}

/**
 * Router state for a link that PUSHES to a sub-page.
 *
 * Spread into `navigate(to, pushState())` or a `<Link state={pushState()}>`.
 * Returns `undefined` off mobile so the state object is absent entirely rather
 * than carrying `slideIn: false`, which keeps `location.state` clean for the
 * cold-load case the flag exists to protect.
 */
export const pushState = (): SlideNavState | undefined =>
  shouldSlide() ? { slideIn: true } : undefined
